import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "BigSystemOne - Restaurant Reputation Management Platform | Reviews, Social Media & Waitlist",
  description = "Manage your restaurant's online reputation from one platform. Monitor reviews, automate social media, streamline waitlists, and create QR code menus. Trusted by restaurants nationwide.",
  keywords = "restaurant reputation management, review monitoring, social media management for restaurants, waitlist management, QR code menu, restaurant marketing, online reviews, Google reviews for restaurants, restaurant table management, restaurant review response, restaurant social media, digital waitlist",
  image = "/Logo%20for%20Big%20System%20One.png",
  url = "https://bigsystemone.com"
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "BigSystemOne",
    "applicationCategory": "BusinessApplication",
    "description": description,
    "operatingSystem": "Web, iOS, Android",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "49",
      "highPrice": "99",
      "priceCurrency": "USD",
      "offerCount": "3"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "BigSystemOne",
      "url": url,
      "logo": image,
      "description": "Restaurant reputation management platform trusted by operators nationwide"
    },
    "featureList": [
      "Review Monitoring & Response",
      "Social Media Management",
      "Waitlist & Table Management",
      "QR Code Digital Menus",
      "Promotions & Discount Codes",
      "Sentiment Analysis",
      "Multi-Location Support",
      "Performance Analytics"
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What review platforms do you support?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "BigSystemOne integrates with Google Reviews, Yelp, TripAdvisor, Facebook, and Instagram. Monitor and respond to reviews from all major platforms in one unified dashboard."
        }
      },
      {
        "@type": "Question",
        "name": "Can I manage multiple restaurant locations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. BigSystemOne supports unlimited locations under a single account. Manage reviews, social media, waitlists, and menus for all your restaurants from one platform."
        }
      },
      {
        "@type": "Question",
        "name": "How does the waitlist system work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Guests join your digital waitlist via a link, QR code, or in person. They receive SMS notifications with real-time wait estimates and are notified when their table is ready."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer a free trial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. All plans include a 14-day free trial with full access to every feature. No credit card required to start."
        }
      },
      {
        "@type": "Question",
        "name": "Can I import my existing menu?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. You can import menus via CSV upload, PDF extraction, or enter items manually. Our system supports bulk import for fast setup."
        }
      },
      {
        "@type": "Question",
        "name": "How do I get started?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Click Start Free Trial, create your account, and connect your review platforms and social media accounts. Most restaurants are fully set up in under 15 minutes."
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="BigSystemOne" />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="BigSystemOne" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content="en-us" />
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Structured Data - Application */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Structured Data - FAQ */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Favicon and App Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/BSO%20Icon.ico" />

      {/* DNS Prefetch for External Resources */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//cdnjs.cloudflare.com" />
    </Helmet>
  );
};

export default SEOHead;
